import React, { useEffect, useMemo, useRef, useState } from "react";

import { handlePrintData, openImage } from "../../../utils/PrintData";
import CardHeading from "../../../shared/CardHeading";
import {
  emrAccidentHeaders,
  emrAccidentValues,
  emrDiagnosisHeaders,
  emrItemsHeaders,
  emrPatientDetailsList,
  fomrateEMRItemsData,
  fomrateSupportingInfoData,
  formatDiagnosisData,
  formatmmunizationData,
  immunizationHeader,
  itemTypeOptions,
  supportingInfoHeaders,
  VitalSignsRecordList,
  vitalSignsValues,
} from "../../../constants/EmrContants";
import { PrintIcon } from "../../../utils/TableActionIcons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { renderIfNotEmpty } from "../../../utils/RenderIfNotEmpty";
import ReportHeader from "../../../shared/ReportHeader";
import ReportFooter from "../../../shared/ReportFooter";
import PatientPrintHeaderBox from "../../../shared/PatientPrintHeaderBox";
import { fetchData } from "../../../helpers/fetchData";
import { DEFAULT_MEDICAL_REPORT_SECTIONS } from "../../../constants/MedicalReportSections";
import ChaiEmrDetails from "./ChaiEmrDetails";
import useMedicalReportSettings from "../../../hooks/useMedicalReportSettings";

const groupEMRItemsByCategory = (items = []) => {
  if (!Array.isArray(items) || !items.length) return []
  const groups = items.reduce((acc, item) => {
    const category = item?.selected_category || 'Others'
    if (!acc[category]) {
      acc[category] = {
        category,
        label:
          itemTypeOptions?.find(opt => opt.value === category)?.label ||
          category,
        items: [],
      }
    }
    acc[category].items.push(item)
    return acc
  }, {})
  return Object.values(groups)
}

const isMedicationItem = item => [item?.selected_category, item?.item_category, item?.item_type]
  .some(value => ['medication-codes', 'medication'].includes(String(value || '').toLowerCase()))

const groupItems = (items, medication) => groupEMRItemsByCategory((items || []).filter(item => isMedicationItem(item) === medication))

const ReportSection = ({ enabled, children }) => enabled ? children : null

function StandardEmrDetails({ emrData }) {
  const printRef = useRef(null);
  const { t, i18n } = useTranslation();
  const lang = i18n?.language;
  const { buisnessInfo } = useSelector((state) => state?.auth);
  const [sections, setSections] = useState(DEFAULT_MEDICAL_REPORT_SECTIONS)
  const practitionerId = emrData?.practitioner?.practitioner_id || emrData?.practitioner?.id || ''

  useEffect(() => {
    let active = true
    if (!practitionerId) return undefined
    fetchData('GET', `/v3/medical-report-settings/practioner/${encodeURIComponent(practitionerId)}`, {})
      .then(response => {
        const configured = response?.data?.sections || response?.data?.data?.sections
        if (active && Array.isArray(configured) && configured.length) {
          setSections(configured.map(section => section?.acc || section))
        }
      })
      .catch(() => { })
    return () => { active = false }
  }, [practitionerId])

  const enabled = useMemo(() => new Set(sections), [sections])

  return (
    <div className="">
      <div
        className={`icon-bg-container absolute top-[21px] ${lang == "ar" ? "left-[70px]" : "right-[70px]"
          }`}
        onClick={() =>
          handlePrintData(printRef.current?.innerHTML, t("Medical Report"))
        }
      >
        <PrintIcon iconSize="20" />
      </div>
      <div className="print-container" ref={printRef}>
        <style>{`
          .emr-html-content h1 { font-size: 1.8em; font-weight: bold; margin: 0.5em 0; display: block; }
          .emr-html-content h2 { font-size: 1.5em; font-weight: bold; margin: 0.4em 0; display: block; }
          .emr-html-content h3 { font-size: 1.3em; font-weight: bold; margin: 0.3em 0; display: block; }
          .emr-html-content h4 { font-size: 1.1em; font-weight: bold; margin: 0.2em 0; display: block; }
          .emr-html-content p { margin-bottom: 0.4em; }
          .emr-html-content strong { font-weight: bold; }
          .emr-html-content em { font-style: italic; }
          .emr-html-content ul { list-style-type: disc; margin-left: 1.5em; margin-bottom: 0.5em; }
          .emr-html-content ol { list-style-type: decimal; margin-left: 1.5em; margin-bottom: 0.5em; }
          .emr-html-content { display: block; width: 100%; }
          .emr-section { page-break-inside: auto; }
          @media print {
            .print-container { width: 100% !important; }
            .emr-print-wrap { width: 92% !important; max-width: 92% !important; margin: 0 auto !important; }
            thead { display: table-header-group !important; }
            .report-print-table { width: 100% !important; border-collapse: collapse !important; border: none !important; margin: 0 !important; padding: 0 !important; }
            .report-print-table > thead > tr > td,
            .report-print-table > tbody > tr > td { padding: 0 !important; border: none !important; background: transparent !important; }
            .emr-section { page-break-inside: auto; }
            .emr-table-wrap { page-break-inside: auto; }
            tr { page-break-inside: avoid; }
            .emr-section { border: 1px solid #d1d5db !important; border-radius: 6px !important; margin-top: 8px !important; }
            .emr-section > div:first-child { background: #f3f4f6 !important; padding: 8px !important; }
            .emr-section table { width: 100% !important; border-collapse: collapse !important; }
            .emr-section th { background: #f9fafb !important; text-align: left !important; }
            .emr-section td, .emr-section th { border: 1px solid #d1d5db !important; padding: 6px 10px !important; font-size: 11px !important; }
          }
        `}</style>
        <div className="emr-print-wrap w-full mx-auto">
          <table className="report-print-table w-full border-collapse">
            <thead>
              <tr>
                <td className="p-0 border-0">
                  <ReportHeader reportName={t("Medical Summary")} />
                  {/* Patient Information box for Page 2+ only (Hidden on screen and covered on Page 1) */}
                  <PatientPrintHeaderBox emrData={emrData} />
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-0 border-0">
                  <div className="flex flex-col gap-3 print:-mt-[46px] print:relative print:z-10 print:bg-white print:pt-[1px]">
          <ReportSection enabled={enabled.has('patient_information')}><InfoTable
            title={`${t("Patient Information")} / معلومات المريض`}
            rows={emrPatientDetailsList(emrData, t)}
          /></ReportSection>
          <ReportSection enabled={enabled.has('vital_signs')}><div className="emr-section border border-gray-300 rounded">
            <div className="bg-gray-100 p-2 rounded">
              <CardHeading
                textClassName="!text-[14px]"
                title={t("vital_signs")}
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 py-2 w-full gap-y-2 px-2">
              {VitalSignsRecordList(
                vitalSignsValues(emrData?.vital_signs),
                t
              )?.map((data, index) => (
                <RowText title={data?.label} value={data?.text} key={index} />
              ))}
            </div>
          </div></ReportSection>

          <ReportSection enabled={enabled.has('diagnosis')}><div className="emr-section border border-gray-300 rounded mt-2">
            <div className="bg-gray-100 p-2 rounded">
              <CardHeading
                textClassName="!text-[14px]"
                title={t("Diagnosis")}
              />
            </div>
            <EmrReportTable
              headers={emrDiagnosisHeaders}
              values={formatDiagnosisData(emrData?.diagnosisArr)}
            />
          </div></ReportSection>
          {enabled.has('items') && renderIfNotEmpty(
            groupItems(emrData?.items, false),
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading textClassName="!text-[14px]" title={t("Service Items")} />
              </div>
              {groupItems(emrData?.items, false)?.map(group => (
                <div key={group?.category}>
                  <div className="flex items-center bg-gray-200 px-4 py-1.5 border-y border-gray-300">
                    <span className="text-14-dark !font-semibold">
                      {t(group?.label)}
                    </span>
                  </div>
                  <EmrReportTable
                    headers={emrItemsHeaders}
                    values={fomrateEMRItemsData(group?.items)}
                  />
                </div>
              ))}
            </div>
          )}
          {enabled.has('medications') && renderIfNotEmpty(
            groupItems(emrData?.items, true),
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded"><CardHeading textClassName="!text-[14px]" title={t("Medications Items")} /></div>
              {groupItems(emrData?.items, true)?.map(group => (
                <div key={group?.category}>
                  <div className="flex items-center bg-gray-200 px-4 py-1.5 border-y border-gray-300"><span className="text-14-dark !font-semibold">{t(group?.label)}</span></div>
                  <EmrReportTable headers={emrItemsHeaders} values={fomrateEMRItemsData(group?.items)} />
                </div>
              ))}
            </div>
          )}
          {enabled.has('supporting_infos') && renderIfNotEmpty(
            emrData?.supportingInfos,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("supporting_infos")}
                />
              </div>
              <EmrReportTable
                headers={supportingInfoHeaders}
                values={fomrateSupportingInfoData(emrData?.supportingInfos)}
              />
            </div>
          )}
          {enabled.has('accident_information') && renderIfNotEmpty(
            emrData?.accident,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("Accident Information")}
                />
              </div>
              <EmrReportTable
                headers={emrAccidentHeaders}
                values={emrAccidentValues([emrData?.accident])}
              />
            </div>
          )}
          {enabled.has('immunization') && renderIfNotEmpty(
            emrData?.immunization,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("immunization")}
                />
              </div>
              <EmrReportTable
                headers={immunizationHeader}
                values={formatmmunizationData([emrData?.immunization])}
              />
            </div>
          )}
          {enabled.has('chief_complaints') && renderIfNotEmpty(
            emrData?.medical_assessments?.chief_complaints?.value,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("chief_complaints")}
                />
              </div>
              <div className="py-2 w-full px-3 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      emrData?.medical_assessments?.chief_complaints?.value ||
                      "__",
                  }}
                ></span>
              </div>
            </div>
          )}
          {enabled.has('family_history') && renderIfNotEmpty(
            emrData?.medical_assessments?.family_history,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("family_history")}
                />
              </div>
              <div className="py-2 w-full px-3 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      emrData?.medical_assessments?.family_history || "__",
                  }}
                ></span>
              </div>
            </div>
          )}
          {enabled.has('past_history') && renderIfNotEmpty(
            emrData?.medical_assessments?.past_history,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("past_history")}
                />
              </div>
              <div className="py-2 w-full px-3 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html: emrData?.medical_assessments?.past_history || "__",
                  }}
                ></span>
              </div>
            </div>
          )}
          {enabled.has('recommendations') && renderIfNotEmpty(
            emrData?.doctor_notes?.recommendation,
            <div className="border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("Recommendations")}
                />
              </div>
              <div className=" py-2 w-full px-2 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html: emrData?.doctor_notes?.recommendation || "__",
                  }}
                ></span>
              </div>
            </div>
          )}
          {enabled.has('physical_assessment') && renderIfNotEmpty(
            emrData?.medical_assessments?.physical_assessment,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("physical_assessment")}
                />
              </div>
              <div className="py-2 w-full px-3 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      emrData?.medical_assessments?.physical_assessment || "__",
                  }}
                ></span>
              </div>
            </div>
          )}
          {enabled.has('significant_signs') && renderIfNotEmpty(
            emrData?.medical_assessments?.significient_signs,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("significant_signs")}
                />
              </div>
              <div className="py-2 w-full px-3 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      emrData?.medical_assessments?.significient_signs || "__",
                  }}
                ></span>
              </div>
            </div>
          )}
          {enabled.has('other_conditions') && renderIfNotEmpty(
            emrData?.medical_assessments?.other_conditions,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("other_condition")}
                />
              </div>
              <div className="py-2 w-full px-3 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      emrData?.medical_assessments?.other_conditions || "__",
                  }}
                ></span>
              </div>
            </div>
          )}
          {enabled.has('progress_notes') && renderIfNotEmpty(
            emrData?.doctor_notes?.progress_notes,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("progress_notes")}
                />
              </div>
              <div className="py-2 w-full px-3 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html: emrData?.doctor_notes?.progress_notes || "__",
                  }}
                ></span>
              </div>
            </div>
          )}
          {enabled.has('procedure_notes') && renderIfNotEmpty(
            emrData?.doctor_notes?.procedure_notes,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("procedure_notes")}
                />
              </div>
              <div className="py-2 w-full px-3 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html: emrData?.doctor_notes?.procedure_notes || "__",
                  }}
                ></span>
              </div>
            </div>
          )}
          {/* {renderIfNotEmpty(
            emrData?.doctor_notes?.nursing_notes,
            <div className="border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("nursing_notes")}
                />
              </div>
              <div className=" py-2 w-full px-2 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html: emrData?.doctor_notes?.nursing_notes || "__",
                  }}
                ></span>
              </div>
            </div>
          )} */}
          {enabled.has('history_and_physical') && renderIfNotEmpty(
            emrData?.doctor_notes?.history_notes,
            <div className="emr-section border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("history_and_physical")}
                />
              </div>
              <div className="py-2 w-full px-3 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html: emrData?.doctor_notes?.history_notes || "__",
                  }}
                ></span>
              </div>
            </div>
          )}
          {/* {renderIfNotEmpty(
            emrData?.doctor_notes?.consult_notes,
            <div className="border border-gray-300 rounded mt-2">
              <div className="bg-gray-100 p-2 rounded">
                <CardHeading
                  textClassName="!text-[14px]"
                  title={t("consult_notes")}
                />
              </div>
              <div className=" py-2 w-full px-2 text-12-dark !font-normal">
                <span
                  className="emr-html-content"
                  dangerouslySetInnerHTML={{
                    __html: emrData?.doctor_notes?.consult_notes || "__",
                  }}
                ></span>
              </div>
            </div>
          )} */}
          {enabled.has('practitioner_signature') && <div className="flex gap-8 w-full justify-end mt-8 print:mt-16">
            <div className="text-center">
              <p className="text-[10px] mt-1">{t("Practitioner Stamp")}</p>
              <img
                src={emrData?.practitioner?.stamp}
                alt={t("stamp")}
                className="w-[7rem] h-[7rem] border-gray-200"
              />
            </div>
            <div className="text-center">
              <p className="text-[10px] mt-1">{t("Practitioner Signature")}</p>
              <img
                src={emrData?.practitioner?.signature}
                alt={t("signature")}
                className="w-[7rem] h-[7rem] border-gray-200"
              />
            </div>
          </div>}
          <ReportFooter />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EmrDetails({ emrData, ...props }) {
  const { isChaiFormat } = useMedicalReportSettings();

  if (isChaiFormat) {
    return <ChaiEmrDetails emrData={emrData} {...props} />;
  }

  return <StandardEmrDetails emrData={emrData} {...props} />;
}

export const RowText = ({ title, value }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center px-2 pb-2 gap-x-2">
      <p className="text-12-dark !font-semibold whitespace-nowrap">{t(title)}:</p>
      <p className="text-12-dark !font-normal break-words">{value}</p>
    </div>
  );
};

export const EmrReportTable = ({ headers, values }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (
    <table className="emr-table-wrap min-w-full border-collapse border border-gray-300 text-sm">
      <thead className="">
        <tr>
          {headers?.map((header, idx) => (
            <th
              key={idx}
              className={`capitalize border bg-gray-50 px-4 py-2 text-darkBg font-semibold ${lang == "ar" ? "text-right" : "text-left"
                }`}
            >
              {t(header)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {values?.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {headers?.map((header, colIndex) => {
              const cellValue = row?.[header];
              return (
                <td
                  key={colIndex}
                  className="border px-4 py-2 text-12-dark !font-normal"
                >
                  {row?.type === "img" && header === "value" ? (
                    <img
                      className="h-[100px]"
                      onClick={() => {
                        openImage(cellValue);
                      }}
                      src={`data:image/png;base64,${cellValue}`}
                      alt={t("attachment")}
                    />
                  ) : (
                    cellValue ?? "--"
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const InfoTable = ({ title, rows, className = "" }) => {
  return (
    <div className={`emr-section border border-gray-300 rounded ${className}`}>
      <div className="bg-gray-100 rounded p-2">
        <span className="text-12-dark !font-semibold">{title}</span>
      </div>
      <table className="emr-table-wrap w-full text-sm">
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`border p-2 ${cellIndex === 0 || cellIndex === 2
                    ? "font-semibold w-1/4 border-l-0"
                    : ""
                    } ${cellIndex === 3 ? "border-r-0" : ""}`}
                >
                  <span
                    className={`text-12-dark ${cell.bold ? "!font-semibold" : "!font-normal"
                      }`}
                  >
                    {cell.content}
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export { ChaiEmrDetails, StandardEmrDetails };
export default EmrDetails;
